/**
 * Contractor Recruitment Page - MaintainX Style
 * Clean, enterprise-grade contractor search and recruitment interface
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Search, MapPin, Phone, Building2, User, FileCheck, Filter,
  ChevronLeft, ChevronRight, Download, Send, CheckCircle2, AlertCircle,
  Loader2, ExternalLink, Users, Mail, RefreshCw, Zap, Shield, Sparkles,
  Globe, ChevronDown, X, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import MaintainXLayout from "@/components/layouts/MaintainXLayout";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// State options
const SUPPORTED_STATES = [
  { code: "WA", name: "Washington", recommended: true, hasAddress: true, hasPhone: true },
  { code: "OR", name: "Oregon", recommended: true, hasAddress: true, hasPhone: true, hasEmail: true },
  { code: "TX", name: "Texas", recommended: false, hasAddress: false, hasPhone: false, hasEnrichment: true }
];

// License type options
const LICENSE_TYPES = [
  { value: "all", label: "All Types" },
  { value: "general", label: "General Contractor" },
  { value: "electrical", label: "Electrical" },
  { value: "hvac", label: "HVAC" },
  { value: "plumbing", label: "Plumbing" }
];

// Status Badge Component - MaintainX style
const StatusBadge = ({ status, className = "" }) => {
  const styles = {
    Active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    Expired: "bg-red-100 text-red-700 border-red-200",
    Pending: "bg-amber-100 text-amber-700 border-amber-200",
    enriched: "bg-blue-100 text-blue-700 border-blue-200",
    no_contact_found: "bg-slate-100 text-slate-600 border-slate-200",
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status] || styles.Active} ${className}`}>
      {status === "enriched" ? "Contact Found" : status === "no_contact_found" ? "No Contact" : status}
    </span>
  );
};

// Confidence Badge
const ConfidenceBadge = ({ score }) => {
  const styles = {
    high: "bg-emerald-100 text-emerald-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-slate-100 text-slate-600",
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${styles[score] || styles.low}`}>
      {score?.toUpperCase() || "LOW"} confidence
    </span>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, trend, color = "blue" }) => {
  const colorStyles = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorStyles[color]}`}>
          <Icon size={20} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value?.toLocaleString() || 0}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
};

// Main Page Component
function ContractorRecruitmentContent() {
  // Search state
  const [searchParams, setSearchParams] = useState({
    state: "WA",
    city: "",
    zip_code: "",
    license_type: "all",
    license_status: "Active",
    limit: 25,
    offset: 0
  });
  
  // Results state
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [fortressFilterActive, setFortressFilterActive] = useState(false);
  const [enrichedCount, setEnrichedCount] = useState(0);
  
  // Selection state
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Enrichment state (for Texas Contact Command)
  const [enriching, setEnriching] = useState(null);

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recruitment/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  // Texas Contact Command - Enrich single contact
  const handleEnrichContact = async (contractor) => {
    setEnriching(contractor.license_number);
    
    try {
      const response = await fetch(`${API_URL}/api/recruitment/texas/enrich-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          license_number: contractor.license_number,
          business_name: contractor.business_name
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        setResults(prev => prev.map(r => 
          r.license_number === contractor.license_number
            ? { 
                ...r, 
                phone: data.phone || r.phone,
                email: data.email || r.email,
                address: data.address || r.address,
                city: data.city || r.city,
                zip_code: data.zip_code || r.zip_code,
                website: data.website || r.website,
                enrichment_status: data.status,
                confidence_score: data.confidence_score
              }
            : r
        ));
        
        if (data.phone || data.email) {
          toast.success(`Found contact info for ${contractor.business_name}!`);
          setEnrichedCount(prev => prev + 1);
        } else {
          toast.info(`No contact info found for ${contractor.business_name}`);
        }
      } else {
        toast.error("Failed to enrich contact");
      }
    } catch (error) {
      toast.error("Enrichment failed: " + error.message);
    } finally {
      setEnriching(null);
    }
  };

  // Texas Contact Command - Bulk enrich
  const handleBulkEnrich = async () => {
    if (selectedContractors.length === 0) {
      toast.error("Please select contractors to enrich");
      return;
    }
    
    const toEnrich = results.filter(r => 
      selectedContractors.includes(r.license_number) && 
      r.enrichment_status !== "enriched"
    );
    
    if (toEnrich.length === 0) {
      toast.info("All selected contractors are already enriched");
      return;
    }
    
    setEnriching("bulk");
    toast.info(`Enriching ${toEnrich.length} contacts...`);
    
    try {
      const response = await fetch(`${API_URL}/api/recruitment/texas/enrich-bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contractors: toEnrich, max_items: 50 })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        const enrichedMap = {};
        data.results.forEach(r => { enrichedMap[r.license_number] = r; });
        
        setResults(prev => prev.map(r => {
          const enriched = enrichedMap[r.license_number];
          if (enriched) {
            return {
              ...r,
              phone: enriched.phone || r.phone,
              email: enriched.email || r.email,
              website: enriched.website || r.website,
              enrichment_status: enriched.status,
              confidence_score: enriched.confidence_score
            };
          }
          return r;
        }));
        
        toast.success(`Enriched ${data.newly_enriched} contacts. ${data.from_cache} from cache.`);
        setEnrichedCount(prev => prev + data.newly_enriched);
      }
    } catch (error) {
      toast.error("Bulk enrichment failed");
    } finally {
      setEnriching(null);
    }
  };

  const handleSearch = async (newOffset = 0) => {
    setLoading(true);
    setSearched(true);
    
    try {
      const params = { ...searchParams, offset: newOffset };
      const response = await fetch(`${API_URL}/api/recruitment/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params)
      });
      
      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
        setTotalCount(data.total_count || 0);
        setSearchParams(prev => ({ ...prev, offset: newOffset }));
        setFortressFilterActive(data.fortress_filter_active || false);
        setSelectedContractors([]);
        setSelectAll(false);
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedContractors([]);
    } else {
      setSelectedContractors(results.map(r => r.license_number));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectContractor = (licenseNumber) => {
    setSelectedContractors(prev => 
      prev.includes(licenseNumber)
        ? prev.filter(ln => ln !== licenseNumber)
        : [...prev, licenseNumber]
    );
  };

  const selectedState = SUPPORTED_STATES.find(s => s.code === searchParams.state);
  const currentPage = Math.floor(searchParams.offset / searchParams.limit) + 1;
  const totalPages = Math.ceil(totalCount / searchParams.limit);

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard icon={Users} label="Total Contractors" value={stats?.total_contractors} color="blue" />
        <StatsCard icon={CheckCircle2} label="Invited" value={stats?.invited_count} color="emerald" />
        <StatsCard icon={Globe} label="States Covered" value={SUPPORTED_STATES.length} color="violet" />
        <StatsCard icon={Zap} label="Contacts Enriched" value={enrichedCount} color="amber" />
      </div>

      {/* Search Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Search Contractors</h3>
          {fortressFilterActive && (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium border border-amber-200">
              <Shield size={16} />
              Fortress Filter Active
            </span>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* State Select */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">State</label>
            <select
              value={searchParams.state}
              onChange={(e) => setSearchParams(prev => ({ ...prev, state: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {SUPPORTED_STATES.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name} {state.recommended ? "★" : ""}
                </option>
              ))}
            </select>
          </div>

          {/* City Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">City</label>
            <Input
              placeholder="Any city"
              value={searchParams.city}
              onChange={(e) => setSearchParams(prev => ({ ...prev, city: e.target.value }))}
              className="h-10"
            />
          </div>

          {/* License Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">License Type</label>
            <select
              value={searchParams.license_type}
              onChange={(e) => setSearchParams(prev => ({ ...prev, license_type: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {LICENSE_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
            <select
              value={searchParams.license_status}
              onChange={(e) => setSearchParams(prev => ({ ...prev, license_status: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Active">Active Only</option>
              <option value="all">All Statuses</option>
            </select>
          </div>

          {/* Search Button */}
          <div className="flex items-end">
            <Button 
              onClick={() => handleSearch(0)}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 h-10"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin mr-2" />
              ) : (
                <Search size={18} className="mr-2" />
              )}
              Search
            </Button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {searched && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Results Header */}
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold text-slate-900">
                {totalCount.toLocaleString()} contractors found
              </h3>
              {selectedContractors.length > 0 && (
                <span className="text-sm text-blue-600 font-medium">
                  {selectedContractors.length} selected
                </span>
              )}
            </div>
            
            {/* Bulk Actions */}
            {selectedContractors.length > 0 && selectedState?.hasEnrichment && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkEnrich}
                  disabled={enriching === "bulk"}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  {enriching === "bulk" ? (
                    <Loader2 size={16} className="animate-spin mr-2" />
                  ) : (
                    <Sparkles size={16} className="mr-2" />
                  )}
                  Find Contacts ({selectedContractors.length})
                </Button>
              </div>
            )}
          </div>

          {/* Results Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Business</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">License</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {results.map((contractor) => (
                  <tr key={contractor.license_number} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedContractors.includes(contractor.license_number)}
                        onChange={() => handleSelectContractor(contractor.license_number)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{contractor.business_name}</p>
                        {contractor.city && (
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                            <MapPin size={12} />
                            {contractor.city}{contractor.zip_code && `, ${contractor.zip_code}`}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-mono text-slate-600">{contractor.license_number}</p>
                      <p className="text-xs text-slate-500">{contractor.license_type || "General"}</p>
                    </td>
                    <td className="px-4 py-3">
                      {contractor.phone || contractor.email ? (
                        <div className="space-y-1">
                          {contractor.phone && (
                            <p className="text-sm text-slate-900 flex items-center gap-1">
                              <Phone size={12} className="text-slate-400" />
                              {contractor.phone}
                            </p>
                          )}
                          {contractor.email && (
                            <p className="text-sm text-slate-600 flex items-center gap-1">
                              <Mail size={12} className="text-slate-400" />
                              {contractor.email}
                            </p>
                          )}
                          {contractor.website && (
                            <a 
                              href={contractor.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                            >
                              <Globe size={12} />
                              Website
                            </a>
                          )}
                        </div>
                      ) : selectedState?.hasEnrichment ? (
                        <span className="text-sm text-slate-400 italic">Click to find</span>
                      ) : (
                        <span className="text-sm text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <StatusBadge status={contractor.license_status || "Active"} />
                        {contractor.confidence_score && (
                          <ConfidenceBadge score={contractor.confidence_score} />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {selectedState?.hasEnrichment && !contractor.phone && !contractor.email ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEnrichContact(contractor)}
                          disabled={enriching === contractor.license_number}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          {enriching === contractor.license_number ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <>
                              <Sparkles size={14} className="mr-1" />
                              Find Contact
                            </>
                          )}
                        </Button>
                      ) : contractor.phone || contractor.email ? (
                        <Button size="sm" variant="outline" className="text-slate-600">
                          <Send size={14} className="mr-1" />
                          Invite
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSearch(Math.max(0, searchParams.offset - searchParams.limit))}
                  disabled={currentPage === 1 || loading}
                >
                  <ChevronLeft size={16} />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSearch(searchParams.offset + searchParams.limit)}
                  disabled={currentPage === totalPages || loading}
                >
                  Next
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {results.length === 0 && !loading && (
            <div className="px-6 py-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search size={28} className="text-slate-400" />
              </div>
              <h4 className="text-lg font-semibold text-slate-900 mb-2">No contractors found</h4>
              <p className="text-slate-500">Try adjusting your search filters</p>
            </div>
          )}
        </div>
      )}

      {/* Initial State */}
      {!searched && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Users size={36} className="text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2">Find Licensed Contractors</h3>
          <p className="text-slate-500 max-w-md mx-auto mb-6">
            Search through thousands of licensed contractors across multiple states. 
            Filter by location, license type, and status to find the perfect match.
          </p>
          <Button onClick={() => handleSearch(0)} className="bg-blue-600 hover:bg-blue-700">
            <Search size={18} className="mr-2" />
            Start Searching
          </Button>
        </div>
      )}
    </div>
  );
}

// Export with MaintainX Layout
export default function ContractorRecruitmentPageV2() {
  return (
    <MaintainXLayout title="Contractor Recruitment" subtitle="Search and recruit licensed contractors">
      <ContractorRecruitmentContent />
    </MaintainXLayout>
  );
}
